import React from 'react'
import { Redirect, useParams } from 'react-router-dom'

import { RouteParams, IS_EMBEDDED, RouteProps } from '@sketch/constants'

import { useForTablet, ErrorMessage } from '@sketch/components'
import { PageCanvas } from './components/PageCanvas'
import DocumentSidebarLayout, {
  DocumentSidebarLayoutHeaderProps,
} from 'containers/DocumentSidebarLayout'
import PageAnnotationsOverlayContext from './components/PageAnnotationsOverlayContext'
import PageCanvasViewHeader from './components/PageCanvasViewHeader'
import {
  isUpgradeToLatestNeeded,
  UpgradeToLatestVersion,
  useVersioning,
} from 'modules/versioning'

import { PageCanvasSupportGate } from './components/PageCanvasSupportGate'
import DocumentSidebar from '../components/DocumentSidebar'
import ShareWorkspaceSidebar from '../components/ShareWorkspaceSidebar'
import DocumentMobileToolbar, {
  isMobileToolboxItem,
} from '../components/DocumentMobileToolbar'
import {
  CanvasRendererProvider,
  useStatus,
} from '@sketch-hq/sketch-web-renderer'
import { useQueryParams } from 'utils'
import usePageZoom from './hooks/usePageZoom'

import { usePlaceDraftAnnotation } from 'modules/annotations/hooks'
import ShareReadyGuard from '../components/ShareReadyGuard'
import { getVersionRenderStatus } from 'utils/renderStatus'
import { useGetVersionQueryWithPolling } from '../operations'
import { StyledWebRendererStatusContainer } from './components/PageCanvas/PageCanvas.styles'
import { ReactComponent as DocumentIcon } from '@sketch/icons/document-64-svg'
import { AnnotationQueryVariablesProvider } from 'modules/annotations/context'

import { PageCanvasSidebar } from './components/PageCanvasSidebar'

import { StyledCanvasViewWrapper } from './PageCanvasView.styles'

const MOBILE_TOOLBAR_ITEMS = ['search', 'zoom', 'add-annotation'].filter(
  isMobileToolboxItem
)

const PageCanvasMobileToolBox = () => {
  const canvasStatus = useStatus()
  const [zoom, setPageZoom] = usePageZoom()
  const [placeDraftAnnotation, setPlaceDraftAnnotation] =
    usePlaceDraftAnnotation() || []

  return (
    <DocumentMobileToolbar
      items={MOBILE_TOOLBAR_ITEMS}
      zoom={zoom}
      setZoom={setPageZoom}
      isDraftAnnotationEnabled={placeDraftAnnotation}
      setPlaceDraftAnnotation={setPlaceDraftAnnotation}
      disableAddAnnotationButton={canvasStatus?.type !== 'READY'}
    />
  )
}

export default function PageCanvasView(
  props: RouteProps<'SHARE_VIEW' | 'SHARE_PAGE_CANVAS_VIEW'>
) {
  const isTabletAndBigger = useForTablet()
  const showPageCanvasSidebar = isTabletAndBigger && !IS_EMBEDDED

  const queryParams = useQueryParams<'SHARE_PAGE_CANVAS_VIEW' | 'SHARE_VIEW'>()
  const params = useParams<
    RouteParams<'SHARE_PAGE_CANVAS_VIEW'> | RouteParams<'SHARE_VIEW'>
  >()

  const versioning = useVersioning()
  const { share, versionShortId, isViewingLatestVersion } = versioning
  const currentVersion = !versioning.loading ? versioning.currentVersion : null

  const { data, error } = useGetVersionQueryWithPolling(
    share.identifier,
    versionShortId
  )

  const permanentPageIdURL = 'pageUUID' in params ? params.pageUUID : undefined
  const firstDocumentPageIdentifier =
    currentVersion?.document?.pages?.entries[0]?.uuid

  /**
   * We should redirect to the SHARE_VIEW when:
   * - The pageUUID matches the permanent identifier of the first page (Consistency)
   * - There's a search query arg and we need to show the results grid
   */
  const shouldRedirectToShareView =
    (firstDocumentPageIdentifier &&
      permanentPageIdURL &&
      permanentPageIdURL === firstDocumentPageIdentifier) ||
    'search' in queryParams

  if (isUpgradeToLatestNeeded(error)) {
    return <UpgradeToLatestVersion error={error} />
  }

  if (shouldRedirectToShareView) {
    const pathname = versioning.getPathname({
      routeKey: 'SHARE_VIEW',
      routeParams: {
        shareID: versioning.share.identifier,
      },
    })

    return <Redirect to={{ ...props.location, pathname }} />
  }

  const disableAnnotations = !share.commentsEnabled || IS_EMBEDDED
  const parsedRenderStatus = getVersionRenderStatus(data?.share?.version)

  const permanentPageId =
    permanentPageIdURL || firstDocumentPageIdentifier || undefined

  return (
    <CanvasRendererProvider>
      <AnnotationQueryVariablesProvider
        shareIdentifier={share.identifier}
        permanentPageId={permanentPageId || ''}
      >
        <PageAnnotationsOverlayContext
          disabled={disableAnnotations}
          permanentPageId={permanentPageId}
          isViewingLatestVersion={isViewingLatestVersion}
          shareIdentifier={share.identifier}
        >
          <DocumentSidebarLayout
            header={headerProps => (
              <PageCanvasViewHeader
                share={share}
                setSidebarLeftOpen={headerProps.setSidebarLeftOpen}
                toggleSidebarRight={headerProps.toggleSidebarRight}
                isSidebarRightOpen={headerProps.isSidebarRightOpen}
                isViewingLatestVersion={isViewingLatestVersion}
                userCanOpenInApp={!!currentVersion?.document?.userCanOpenInApp}
              />
            )}
            sidebarLeft={
              <ShareWorkspaceSidebar
                workspaceIdentifier={share.workspace.identifier}
              />
            }
            sidebarRight={({
              toggleSidebarRight,
            }: DocumentSidebarLayoutHeaderProps) => (
              <DocumentSidebar
                share={share}
                permanentPageId={permanentPageId}
                userCanSeeComments
                toggleSidebar={toggleSidebarRight}
              />
            )}
          >
            <PageCanvasSupportGate>
              <ShareReadyGuard renderStatus={parsedRenderStatus}>
                {permanentPageId ? (
                  <StyledCanvasViewWrapper>
                    {showPageCanvasSidebar && <PageCanvasSidebar />}
                    <PageCanvas permanentPageIdentifier={permanentPageId!} />
                  </StyledCanvasViewWrapper>
                ) : (
                  <StyledWebRendererStatusContainer>
                    <ErrorMessage
                      title="No pages in document"
                      icon={<DocumentIcon />}
                      iconSize="medium"
                    >
                      No pages have been added here yet.
                    </ErrorMessage>
                  </StyledWebRendererStatusContainer>
                )}
              </ShareReadyGuard>
            </PageCanvasSupportGate>
            {!IS_EMBEDDED && !isTabletAndBigger && <PageCanvasMobileToolBox />}
          </DocumentSidebarLayout>
        </PageAnnotationsOverlayContext>
      </AnnotationQueryVariablesProvider>
    </CanvasRendererProvider>
  )
}
